export interface Insurance {
  ID: string;
  Revision: number;
  CreatedAt: string;
  UpdatedAt: string;
  MarketCountry: string;
  BrokerPolicyID?: string;
  BrokerClientID?: string;
  BrokerCustomerID?: string;
  CustomerID?: string;
  PersonID?: string;
  Status?: string;
  Amount?: number;
  SignedAt?: string;
  TerminatedAt?: string;
  TerminationReceivedAt?: string;
  TerminationReason?: string;
  AccountNumber?: string;
  ClearingNumber?: string;
  PaymentSetupAt?: string;
  PaymentSetupIgnored?: boolean;
}

export interface InsuranceAgreementSigningToken {
  InsurancePolicyID: string;
  CreatedAt: string;
  AgreementSigningToken: string;
}

export interface GetInsuranceParams {
  status?: 'active' | 'new' | 'terminated';
  brokerPolicyID?: string;
  brokerCustomerID?: string;
  personID?: string | null;
  customerID?: string;
}

export enum InsuranceTerminateReason {
  'customer-withdrew' = 'customer-withdrew',
  'customer-terminated' = 'customer-terminated',
  'age' = 'age',
  'death' = 'death',
  'insolvency' = 'insolvency',
  'multiple-policies' = 'multiple-policies',
  'screening' = 'screening',
  'max-compensation' = 'max-compensation',
  'emigration' = 'emigration',
}

export interface TerminateRequest {
  Reason: string;
  TerminatedAt?: string;
}

export interface InsuranceTerminateParams {
  id: string;
  reason: InsuranceTerminateReason;
  at: Date | null;
}

export interface InsurancePersonIsEligibleParams {
  PersonID: string;
  PersonAge: string;
  EmploymentStatus: string;
  MarketCountry: string;
}

export enum InsuranceEligibilityStatus {
  APPROVED = 'approved',
  DENIED = 'denied',
  NEW = 'new',
}

export interface InsuranceGetEligibilityV2QueryParams {
  CustomerID?: string;
  ClientID?: string;
  PersonID: Array<string>;
  LoanApplicationID?: Array<string>;
  MarketCountry?: string;
  Status?: Array<InsuranceEligibilityStatus>;
  Limit?: number;
  Offset?: number;
}

export interface InsurancePersonIsEligibleV2Params {
  Brand?: string;
  CustomerID: string;
  ClientID: string;
  PersonID: string;
  LoanApplicationID: string;
  MarketCountry: string;
  Source: string;
  FirstName: string;
  LastName: string;
}

export interface InsurancePersonIsEligible {
  isEligible: boolean;
}

export interface InsurancePersonIsEligibleV2 {
  ID: string;
  CustomerID: string;
  PersonID: string;
  ClientID: string;
  LoanApplicationID: string;
  MarketCountry: string;
  Source: string;
  Status: string;
  DenyReason: string;
  CreatedAt: string;
  UpdatedAt: string;
  AnonymizedAt: string;
  FirstName: string;
  LastName: string;
}

export type CreateInsuranceEligibilityDto = InsurancePersonIsEligibleV2Params;
export type InsuranceEligibility = InsurancePersonIsEligibleV2;

// ─── Clean V2 Eligibility Types (from OpenAPI spec) ───

/**
 * Request body for creating/checking insurance eligibility
 * @see POST /v2/eligibility
 */
export interface CreateEligibilityRequest {
  PersonID: string;
  CustomerID?: string;
  LoanApplicationID?: string;
  MarketCountry?: string;
  ClientID?: string;
  Source?: string;
  /** Optional — backend resolves from PersonID. Only needed for token types without a person DB record. */
  FirstName?: string;
  /** Optional — backend resolves from PersonID. Only needed for token types without a person DB record. */
  LastName?: string;
  /**
   * BE-required as of MV-2634 (eligibility resolves from `PersonID + Brand`).
   * Kept optional on the FE type until the FE brand-source refactor lands
   * separately — current call site sends `applicationData?.Brand` which can be
   * undefined for some token-only paths. The brand-source incident two
   * sprints ago cost ~80 contracts; do not change brand resolution here.
   */
  Brand?: string;
}

export type BillogramMandateExternalStatus =
  | 'inactive'
  | 'active'
  | 'terminated'
  | '';

/**
 * Subset of the Billogram recurring mandate embedded on the eligibility
 * response when the user has an active insurance policy backed by a Billogram
 * mandate. Markets that don't use Billogram (today: FI) never receive this.
 */
export interface BillogramMandateSubset {
  ID: string;
  PolicyID: string;
  PersonID: string;
  MarketCountry: string;
  Amount: number;
  PaymentURL: string;
  ExternalStatus: BillogramMandateExternalStatus;
  CreatedAt: string;
  UpdatedAt: string;
}

/**
 * Tagged union for the mandate attached to an active insurance policy.
 * Today only `billogram-recurring` exists; future providers add new variants.
 */
export type MandateDetails = {
  Kind: 'billogram-recurring';
  Mandate: BillogramMandateSubset;
};

/**
 * Insurance eligibility response
 * @see /v2/eligibility
 */
export interface Eligibility {
  ID: string;
  CustomerID: string;
  PersonID: string;
  ClientID: string;
  LoanApplicationID: string;
  MarketCountry: string;
  Source: string;
  Status: InsuranceEligibilityStatus;
  DenyReason?: string;
  CreatedAt: string;
  UpdatedAt: string;
  AnonymizedAt: string;
  FirstName?: string;
  LastName?: string;
  MandateDetails?: MandateDetails;
}
